import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { teamsApi } from "@/lib/api";
import { toast } from "sonner";
import { X, Copy, Check } from "lucide-react";

interface ResetPasswordModalProps {
  teamId: string;
  member: { id: string; email: string; display_name: string };
  onClose: () => void;
}

export default function ResetPasswordModal({
  teamId,
  member,
  onClose,
}: ResetPasswordModalProps) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [newPwd, setNewPwd] = useState<string | null>(null);
  const [pwdCopied, setPwdCopied] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await teamsApi.resetMemberPassword(teamId, member.id);
      setNewPwd(res.initial_password);
      toast.success(t("teamMgmt.resetPwdSuccess"));
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || t("teamMgmt.resetPwdFailed")
      );
    } finally {
      setBusy(false);
    }
  };

  const copyPwd = () => {
    if (!newPwd) return;
    navigator.clipboard.writeText(newPwd);
    setPwdCopied(true);
    setTimeout(() => setPwdCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-[480px] mx-4 sci-card p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2
            className="text-base font-semibold text-white"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            {t("teamMgmt.resetPwdConfirmTitle")}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        {newPwd ? (
          <>
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
                {t("teamMgmt.pwdOnceWarn")}
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-20">User</span>
                  <span className="text-white">{member.display_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-20">Email</span>
                  <span className="text-white font-mono">{member.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-20">Password</span>
                  <code className="flex-1 px-2 py-1.5 rounded bg-black/40 border border-white/10 text-emerald-300 font-mono text-sm select-all">
                    {newPwd}
                  </code>
                  <button
                    onClick={copyPwd}
                    title="Copy password"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    {pwdCopied ? (
                      <Check size={14} className="text-emerald-400" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 pt-2">
                  {t("teamMgmt.mustChangePwd")}
                </p>
              </div>
            </div>
            <div className="flex gap-3 pt-5">
              <Button onClick={onClose} className="flex-1 btn-primary-sci">
                {t("teamMgmt.done")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-400 leading-relaxed mb-4">
              {t("teamMgmt.resetPwdConfirmBody")}
            </p>
            <div className="space-y-2 text-xs mb-4">
              <div className="flex items-center gap-2">
                <span className="text-slate-500 w-20">User</span>
                <span className="text-white">{member.display_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 w-20">Email</span>
                <span className="text-white font-mono">{member.email}</span>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="ghost"
                onClick={onClose}
                className="flex-1 text-slate-400 hover:text-white border border-white/10"
              >
                {t("teamMgmt.cancel")}
              </Button>
              <Button
                onClick={submit}
                disabled={busy}
                className="flex-1 btn-primary-sci"
              >
                {busy ? "..." : t("teamMgmt.resetPwdConfirm")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
